import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api from '../../services/api';
import footerService from '../../services/footer';
import SectionManager from '../../components/admin/SectionManager';

const emptyProductForm = {
  name: '', slug: '', description: '', shortDescription: '', price: '',
  originalPrice: '', category: '', brand: '', images: [], sizes: '', colors: '',
  variants: [], stockQuantity: '', leftBadge: '', rightBadge: '', isFeatured: false, isBestSeller: false, isNew: false
};

const emptyCategoryForm = { name: '', slug: '', description: '', image: null };
const emptyHeroForm = { title: '', imageUrl: '', mobileImageUrl: '', link: '', order: 0 };
const emptyMegaMenuForm = { navItem: '', columns: [] };
const emptyColumn = { title: '', items: [], shopAll: false, shopAllLink: '', highlightFirst: false };
const emptyItem = { name: '', link: '' };
const emptyCouponForm = { code: '', discountType: 'percentage', discountValue: '', minCartAmount: '', expiryDate: '' };

const slugify = (text) => text.toString().toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
const parseList = (value) => value ? value.split(',').map(item => item.trim()).filter(Boolean) : [];

const formatToCommaString = (value) => {
  if (!value) return '';
  if (Array.isArray(value)) {
    const flat = value.map(item => {
      if (typeof item === 'string') {
        try {
          const parsed = JSON.parse(item);
          if (Array.isArray(parsed)) return parsed.join(', ');
          return String(parsed);
        } catch { return item; }
      }
      return String(item);
    }).join(', ');
    return flat;
  }
  if (typeof value === 'string') {
    try {
      let parsed = JSON.parse(value);
      while (typeof parsed === 'string' && (parsed.startsWith('[') || parsed.startsWith('{'))) {
        parsed = JSON.parse(parsed);
      }
      if (Array.isArray(parsed)) return parsed.join(', ');
      return String(parsed);
    } catch {
      return value;
    }
  }
  return '';
};

const UserDashboard = () => {
  const { user, logout, isAdmin } = useAuth();
  const { theme, updateTheme } = useTheme();

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [megaMenus, setMegaMenus] = useState([]);
  const [heroImages, setHeroImages] = useState([]);
  const [navItems, setNavItems] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  
  const [footerData, setFooterData] = useState({ columns: [], bottomLinks: [], socialLinks: [], contactInfo: {} });

  const [productForm, setProductForm] = useState(emptyProductForm);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productImageFiles, setProductImageFiles] = useState([]);
  const [uploadingProduct, setUploadingProduct] = useState(false);

  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryImageFile, setCategoryImageFile] = useState(null);
  const [uploadingCategory, setUploadingCategory] = useState(false);

  const [heroForm, setHeroForm] = useState(emptyHeroForm);
  const [editingHeroId, setEditingHeroId] = useState(null);
  const [uploadingHero, setUploadingHero] = useState(false);

  const [megaMenuForm, setMegaMenuForm] = useState(emptyMegaMenuForm);
  const [editingMegaMenuId, setEditingMegaMenuId] = useState(null);
  const [megaMenuLoading, setMegaMenuLoading] = useState(false);
  const [newNavItem, setNewNavItem] = useState('');
  const [newNavItemPink, setNewNavItemPink] = useState(false);
  const [newNavItemBold, setNewNavItemBold] = useState(false);

  const [couponForm, setCouponForm] = useState(emptyCouponForm);
  const [uploadingCoupon, setUploadingCoupon] = useState(false);

  const [announcementText, setAnnouncementText] = useState('');
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(3500);
  const [saleBoxEnabled, setSaleBoxEnabled] = useState(true);
  const [saleBoxContent, setSaleBoxContent] = useState('⚠️ LAST WEEK OF SALE ⚠️');
  const [saleBoxColor, setSaleBoxColor] = useState('#dc2626');
  const [visualSearchEnabled, setVisualSearchEnabled] = useState(true);
  const [visualSearchColor, setVisualSearchColor] = useState('#d60167');
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [announcementBarEnabled, setAnnouncementBarEnabled] = useState(true);
  const [announcementBarText, setAnnouncementBarText] = useState('ONLINE EXCLUSIVE SALE IS LIVE NOW');
  const [announcementBarColor, setAnnouncementBarColor] = useState('#000000');
  const [checkoutSidebarColor, setCheckoutSidebarColor] = useState('#faf4f8');
  const [continueShoppingLink, setContinueShoppingLink] = useState('/category/new-in');
  const [shippingCost, setShippingCost] = useState(199);
  const [savingSettings, setSavingSettings] = useState(false);

  const [blogForm, setBlogForm] = useState({ title: '', slug: '', content: '', excerpt: '', image: '', status: 'draft' });
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [uploadingBlogImage, setUploadingBlogImage] = useState(false);

  const [themeForm, setThemeForm] = useState({
    primaryColor: theme?.primaryColor || '#d60167',
    secondaryColor: theme?.secondaryColor || '#000000',
    backgroundColor: theme?.backgroundColor || '#ffffff',
    textColor: theme?.textColor || '#1c1d1d',
    borderColor: theme?.borderColor || '#e5e5e5',
    mutedText: theme?.mutedText || '#888888'
  });

  const [showCardPayment, setShowCardPayment] = useState(
    localStorage.getItem('showCardPayment') !== 'false'
  );

  const canCancel = (order) => {
    const status = order.orderStatus || order.status;
    return !['delivered', 'cancelled', 'shipped', 'out_for_delivery'].includes(status);
  };

  const handleCancelOrder = async (orderId, reason = 'Customer cancelled') => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    setCancellingOrderId(orderId);
    try {
      await api.put(`/orders/${orderId}/cancel`, { reason });
      toast.success('Order cancelled successfully');
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order._id === orderId 
            ? { ...order, orderStatus: 'cancelled', cancelledAt: new Date() }
            : order
        )
      );
      setCancellingOrderId(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel order');
      setCancellingOrderId(null);
    }
  };

  const isAdminOrSubAdmin = user?.role === 'admin' || user?.role === 'sub-admin';
  const isFullAdmin = user?.role === 'admin';

  const tabs = useMemo(() => {
    if (isAdminOrSubAdmin) {
      const baseTabs = [
        { key: 'overview', label: 'Overview', icon: 'fa-solid fa-house' },
        { key: 'products', label: 'Products', icon: 'fa-solid fa-box' },
        { key: 'categories', label: 'Categories', icon: 'fa-solid fa-folder' },
        { key: 'orders', label: 'Orders', icon: 'fa-solid fa-bag-shopping' },
        { key: 'blogs', label: 'Blogs', icon: 'fa-solid fa-newspaper' },
      ];
      if (isFullAdmin) {
        baseTabs.push(
          { key: 'heroImages', label: 'Hero Images', icon: 'fa-solid fa-image' },
          { key: 'megamenus', label: 'Mega Menus', icon: 'fa-solid fa-table-cells-large' },
          { key: 'sections', label: 'Home Sections', icon: 'fa-solid fa-layer-group' },
          { key: 'coupons', label: 'Coupons & Discounts', icon: 'fa-solid fa-ticket' },
          { key: 'footer', label: 'Footer Settings', icon: 'fa-solid fa-wrench' },
          { key: 'settings', label: 'Store Settings', icon: 'fa-solid fa-gear' },
          { key: 'theme', label: 'Website Colors', icon: 'fa-solid fa-palette' },
          { key: 'users', label: 'Users', icon: 'fa-solid fa-users' },
          { key: 'newsletter', label: 'Newsletter Subscribers', icon: 'fa-solid fa-envelope' }
        );
      }
      return baseTabs;
    }
    return [
      { key: 'overview', label: 'Overview', icon: 'fa-solid fa-house' },
      { key: 'orders', label: 'My Orders', icon: 'fa-solid fa-bag-shopping' },
      { key: 'profile', label: 'My Profile', icon: 'fa-solid fa-user' }
    ];
  }, [isAdminOrSubAdmin, isFullAdmin]);

  const fetchFooter = async () => {
    try {
      const res = await footerService.getFooter();
      setFooterData(res);
    } catch (err) {
      console.error('Failed to fetch footer:', err);
    }
  };

  const fetchDashboardData = async () => {
    if (!isAdminOrSubAdmin) {
      try {
        const myOrders = await api.get('/orders/my-orders');
        setOrders(Array.isArray(myOrders) ? myOrders : myOrders?.orders || []);
      } catch (err) { console.error(err); }
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const endpoints = [
        api.get('/products'),
        api.get('/categories'),
        api.get('/orders'),
        api.get('/megamenu/all'),
        api.get('/heroes'),
        api.get('/nav-items'),
        api.get('/coupons'),
        api.get('/settings/announcement'),
        api.get('/blogs'),
        footerService.getFooter(),
        api.get('/newsletter/subscribers')
      ];
      if (isFullAdmin) {
        endpoints.push(api.get('/users'));
      }
      const results = await Promise.allSettled(endpoints);
      const [
        productsRes, categoriesRes, ordersRes, megaMenusRes, heroesRes, navItemsRes, couponsRes, settingsRes, blogsRes, footerRes, subscribersRes, usersRes
      ] = results;

      if (productsRes.status === 'fulfilled') setProducts(Array.isArray(productsRes.value) ? productsRes.value : productsRes.value?.products || []);
      if (categoriesRes.status === 'fulfilled') setCategories(Array.isArray(categoriesRes.value) ? categoriesRes.value : categoriesRes.value?.categories || []);
      if (ordersRes.status === 'fulfilled') setOrders(Array.isArray(ordersRes.value) ? ordersRes.value : ordersRes.value?.orders || []);
      if (megaMenusRes.status === 'fulfilled') setMegaMenus(Array.isArray(megaMenusRes.value) ? megaMenusRes.value : megaMenusRes.value?.megamenus || []);
      if (heroesRes.status === 'fulfilled') setHeroImages(Array.isArray(heroesRes.value) ? heroesRes.value : heroesRes.value?.heroes || []);
      if (navItemsRes.status === 'fulfilled') setNavItems(Array.isArray(navItemsRes.value) ? navItemsRes.value : navItemsRes.value?.navItems || []);
      if (couponsRes.status === 'fulfilled') setCoupons(Array.isArray(couponsRes.value) ? couponsRes.value : []);
      if (settingsRes.status === 'fulfilled') {
        setAnnouncementText(settingsRes.value?.text || '');
        setFreeShippingThreshold(settingsRes.value?.freeShippingThreshold || 3500);
        setSaleBoxEnabled(settingsRes.value?.saleBoxEnabled !== undefined ? settingsRes.value.saleBoxEnabled : true);
        setSaleBoxContent(settingsRes.value?.saleBoxContent || '⚠️ LAST WEEK OF SALE ⚠️');
        setSaleBoxColor(settingsRes.value?.saleBoxColor || '#dc2626');
        setVisualSearchEnabled(settingsRes.value?.visualSearchEnabled !== undefined ? settingsRes.value.visualSearchEnabled : true);
        setVisualSearchColor(settingsRes.value?.visualSearchColor || '#d60167');
        setLogoUrl(settingsRes.value?.logoUrl || '/logo.png');
        setAnnouncementBarEnabled(settingsRes.value?.announcementBarEnabled !== undefined ? settingsRes.value.announcementBarEnabled : true);
        setAnnouncementBarText(settingsRes.value?.announcementBarText || 'ONLINE EXCLUSIVE SALE IS LIVE NOW');
        setAnnouncementBarColor(settingsRes.value?.announcementBarColor || '#000000');
        setCheckoutSidebarColor(settingsRes.value?.checkoutSidebarColor || '#faf4f8');
        setContinueShoppingLink(settingsRes.value?.continueShoppingLink || '/category/new-in');
        setShippingCost(settingsRes.value?.shippingCost || 199);
      }
      if (blogsRes.status === 'fulfilled') setBlogs(Array.isArray(blogsRes.value) ? blogsRes.value : []);
      if (footerRes.status === 'fulfilled') setFooterData(footerRes.value);
      if (subscribersRes.status === 'fulfilled') setSubscribers(Array.isArray(subscribersRes.value) ? subscribersRes.value : []);
      if (usersRes && usersRes.status === 'fulfilled') setUsers(Array.isArray(usersRes.value) ? usersRes.value : []);
    } catch (err) { setError('Dashboard data load failed.'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchDashboardData(); }, [isAdminOrSubAdmin]);

  const handleFooterUpdate = async () => {
    try {
      await footerService.updateFooter(footerData);
      toast.success('Footer updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update footer');
    }
  };

  const handleAddColumn = () => {
    setFooterData(prev => ({
      ...prev,
      columns: [...prev.columns, { title: 'New Column', items: [] }]
    }));
  };

  const handleRemoveColumn = (index) => {
    setFooterData(prev => ({
      ...prev,
      columns: prev.columns.filter((_, i) => i !== index)
    }));
  };

  const handleAddItemToColumn = (colIndex) => {
    setFooterData(prev => {
      const newColumns = [...prev.columns];
      newColumns[colIndex].items.push({ name: '', link: '' });
      return { ...prev, columns: newColumns };
    });
  };

  const handleRemoveItemFromColumn = (colIndex, itemIndex) => {
    setFooterData(prev => {
      const newColumns = [...prev.columns];
      newColumns[colIndex].items.splice(itemIndex, 1);
      return { ...prev, columns: newColumns };
    });
  };

  const handleProductChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      slug: name === 'name' && !editingProductId ? slugify(value) : prev.slug
    }));
  };

  const handleProductImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const total = productImageFiles.length + files.length;
    if (total > 20) {
      toast.error(`You can upload maximum 20 images. Current: ${productImageFiles.length}, Selected: ${files.length}`);
      e.target.value = '';
      return;
    }
    setProductImageFiles(prev => [...prev, ...files]);
    const previews = files.map(f => URL.createObjectURL(f));
    setProductForm(prev => ({ ...prev, images: [...prev.images, ...previews] }));
  };

  const removeProductImage = (index) => {
    setProductImageFiles(prev => prev.filter((_, i) => i !== index));
    setProductForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleProductVariantChange = (index, field, value) => {
    const updatedVariants = [...productForm.variants];
    updatedVariants[index][field] = value;
    setProductForm(prev => ({ ...prev, variants: updatedVariants }));
  };

  const addVariantRow = () => {
    setProductForm(prev => ({
      ...prev,
      variants: [...prev.variants, { size: '', color: '', quantity: 0 }]
    }));
  };

  const removeVariantRow = (index) => {
    setProductForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const generateVariantsFromSizesColors = () => {
    const sizes = parseList(productForm.sizes);
    const colors = parseList(productForm.colors);
    if (sizes.length === 0 && colors.length === 0) {
      toast.error('Please enter at least one size or color');
      return;
    }
    const newVariants = [];
    if (sizes.length > 0 && colors.length > 0) {
      for (const size of sizes) {
        for (const color of colors) {
          newVariants.push({ size, color, quantity: 0 });
        }
      }
    } else if (sizes.length > 0) {
      for (const size of sizes) {
        newVariants.push({ size, color: '', quantity: 0 });
      }
    } else if (colors.length > 0) {
      for (const color of colors) {
        newVariants.push({ size: '', color, quantity: 0 });
      }
    }
    setProductForm(prev => ({ ...prev, variants: newVariants }));
    toast.success('Variants generated from sizes/colors');
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.name || !productForm.price || !productForm.category) return toast.error('Name, price and category required');
    const formData = new FormData();
    formData.append('name', productForm.name);
    formData.append('slug', productForm.slug);
    formData.append('description', productForm.description || '');
    formData.append('shortDescription', productForm.shortDescription || '');
    formData.append('price', productForm.price);
    formData.append('originalPrice', productForm.originalPrice || '');
    formData.append('category', productForm.category);
    formData.append('brand', productForm.brand || '');
    const sizesArray = parseList(productForm.sizes);
    const colorsArray = parseList(productForm.colors);
    formData.append('sizes', JSON.stringify(sizesArray));
    formData.append('colors', JSON.stringify(colorsArray));
    // Append variants
    if (productForm.variants && productForm.variants.length > 0) {
      formData.append('variants', JSON.stringify(productForm.variants));
    } else {
      formData.append('variants', '[]');
    }
    formData.append('stockQuantity', productForm.stockQuantity);
    formData.append('leftBadge', productForm.leftBadge || '');
    formData.append('rightBadge', productForm.rightBadge || '');
    formData.append('isFeatured', productForm.isFeatured);
    formData.append('isBestSeller', productForm.isBestSeller);
    formData.append('isNew', productForm.isNew);
    productImageFiles.forEach(file => formData.append('images', file));

    setUploadingProduct(true);
    try {
      if (editingProductId) {
        await api.put(`/products/${editingProductId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Product updated');
      } else {
        await api.post('/products', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Product added');
      }
      setProductForm(emptyProductForm);
      setEditingProductId(null);
      setProductImageFiles([]);
      fetchDashboardData();
    } catch (err) { toast.error(err.response?.data?.message || 'Operation failed'); } finally { setUploadingProduct(false); }
  };

  const handleEditProduct = (product) => {
    setEditingProductId(product._id);
    setProductForm({
      name: product.name || '',
      slug: product.slug || '',
      description: product.description || '',
      shortDescription: product.shortDescription || '',
      price: product.price || '',
      originalPrice: product.originalPrice || '',
      category: product.category?._id || product.category || '',
      brand: product.brand || '',
      images: product.images || [],
      sizes: formatToCommaString(product.sizes),
      colors: formatToCommaString(product.colors),
      variants: product.variants || [],
      stockQuantity: product.stockQuantity || '',
      leftBadge: product.leftBadge || '',
      rightBadge: product.rightBadge || '',
      isFeatured: Boolean(product.isFeatured),
      isBestSeller: Boolean(product.isBestSeller),
      isNew: Boolean(product.isNew)
    });
    setProductImageFiles([]);
    setActiveTab('products');
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Product deleted');
      fetchDashboardData();
    } catch (err) { toast.error('Delete failed'); }
  };

  const handleCategoryChange = (e) => {
    const { name, value } = e.target;
    setCategoryForm(prev => ({
      ...prev,
      [name]: value,
      slug: name === 'name' && !editingCategoryId ? slugify(value) : prev.slug
    }));
  };

  const handleCategoryImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCategoryImageFile(file);
      setCategoryForm(prev => ({ ...prev, image: URL.createObjectURL(file) }));
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryForm.name || !categoryForm.slug) return toast.error('Name and slug required');
    const formData = new FormData();
    formData.append('name', categoryForm.name);
    formData.append('slug', categoryForm.slug);
    formData.append('description', categoryForm.description || '');
    if (categoryImageFile) formData.append('image', categoryImageFile);
    setUploadingCategory(true);
    try {
      if (editingCategoryId) {
        await api.put(`/categories/${editingCategoryId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Category updated');
      } else {
        await api.post('/categories', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Category added');
      }
      setCategoryForm(emptyCategoryForm);
      setEditingCategoryId(null);
      setCategoryImageFile(null);
      fetchDashboardData();
    } catch (err) { toast.error('Save failed'); } finally { setUploadingCategory(false); }
  };

  const handleEditCategory = (category) => {
    setEditingCategoryId(category._id);
    setCategoryForm({
      name: category.name || '',
      slug: category.slug || '',
      description: category.description || '',
      image: category.image || ''
    });
    setCategoryImageFile(null);
    setActiveTab('categories');
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Category deleted');
      fetchDashboardData();
    } catch (err) { toast.error('Delete failed'); }
  };

  const handleHeroChange = (e) => setHeroForm({ ...heroForm, [e.target.name]: e.target.value });

  const handleHeroImageUpload = async (file, isMobile = false) => {
    if (!file) return;
    setUploadingHero(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await api.post('/upload/hero', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (isMobile) {
        setHeroForm(prev => ({ ...prev, mobileImageUrl: res.imageUrl }));
      } else {
        setHeroForm(prev => ({ ...prev, imageUrl: res.imageUrl }));
      }
      toast.success('Image uploaded');
    } catch (err) { toast.error('Upload failed'); } finally { setUploadingHero(false); }
  };

  const handleHeroSubmit = async (e) => {
    e.preventDefault();
    if (!heroForm.imageUrl) return toast.error('Please upload a desktop image');
    try {
      if (editingHeroId) await api.put(`/heroes/${editingHeroId}`, heroForm);
      else await api.post('/heroes', heroForm);
      toast.success(editingHeroId ? 'Hero updated' : 'Hero added');
      setHeroForm(emptyHeroForm);
      setEditingHeroId(null);
      fetchDashboardData();
    } catch (err) { toast.error('Save failed'); }
  };

  const handleEditHero = (hero) => {
    setEditingHeroId(hero._id);
    setHeroForm({
      title: hero.title || '',
      imageUrl: hero.imageUrl || '',
      mobileImageUrl: hero.mobileImageUrl || '',
      link: hero.link || '',
      order: hero.order || 0
    });
    setActiveTab('heroImages');
  };

  const handleDeleteHero = async (id) => {
    if (!window.confirm('Delete this hero image?')) return;
    try {
      await api.delete(`/heroes/${id}`);
      toast.success('Hero deleted');
      fetchDashboardData();
    } catch (err) { toast.error('Delete failed'); }
  };

  const handleCouponChange = (e) => setCouponForm({ ...couponForm, [e.target.name]: e.target.value });

  const handleCouponSubmit = async (e) => {
    e.preventDefault();
    if (!couponForm.code || !couponForm.discountValue) return toast.error('Code and Value are required');
    setUploadingCoupon(true);
    try {
      await api.post('/coupons', couponForm);
      toast.success('Coupon created successfully');
      setCouponForm(emptyCouponForm);
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create coupon');
    } finally {
      setUploadingCoupon(false);
    }
  };

  const handleDeleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await api.delete(`/coupons/${id}`);
      toast.success('Coupon deleted');
      fetchDashboardData();
    } catch (err) { toast.error('Failed to delete coupon'); }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await api.post('/upload/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post('/settings/announcement', { logoUrl: res.imageUrl });
      toast.success('Logo uploaded and saved');
      await fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.post('/settings/announcement', { 
        text: announcementText, 
        freeShippingThreshold: freeShippingThreshold,
        saleBoxEnabled: saleBoxEnabled,
        saleBoxContent: saleBoxContent,
        saleBoxColor: saleBoxColor,
        visualSearchEnabled: visualSearchEnabled,
        visualSearchColor: visualSearchColor,
        logoUrl: logoUrl,
        announcementBarEnabled: announcementBarEnabled,
        announcementBarText: announcementBarText,
        announcementBarColor: announcementBarColor,
        checkoutSidebarColor: checkoutSidebarColor,
        continueShoppingLink: continueShoppingLink,
        shippingCost: shippingCost
      });
      toast.success('Settings updated successfully');
    } catch (err) {
      toast.error('Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleBlogChange = (e) => {
    const { name, value } = e.target;
    setBlogForm(prev => ({
      ...prev,
      [name]: value,
      slug: name === 'title' && !editingBlogId ? slugify(value) : prev.slug
    }));
  };

  const handleBlogImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingBlogImage(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/upload/hero', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBlogForm(prev => ({ ...prev, image: res.imageUrl }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingBlogImage(false);
    }
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    if (!blogForm.title || !blogForm.content) {
      toast.error('Title and content are required');
      return;
    }
    try {
      if (editingBlogId) {
        await api.put(`/blogs/${editingBlogId}`, blogForm);
        toast.success('Blog updated');
      } else {
        await api.post('/blogs', blogForm);
        toast.success('Blog created');
      }
      setBlogForm({ title: '', slug: '', content: '', excerpt: '', image: '', status: 'draft' });
      setEditingBlogId(null);
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleEditBlog = (blog) => {
    setEditingBlogId(blog._id);
    setBlogForm({
      title: blog.title || '',
      slug: blog.slug || '',
      content: blog.content || '',
      excerpt: blog.excerpt || '',
      image: blog.image || '',
      status: blog.status || 'draft'
    });
    setActiveTab('blogs');
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm('Delete this blog?')) return;
    try {
      await api.delete(`/blogs/${id}`);
      toast.success('Blog deleted');
      fetchDashboardData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleThemeSubmit = async (e) => {
    e.preventDefault();
    const result = await updateTheme(themeForm);
    if (result.success) toast.success('Theme colors updated');
    else toast.error(result.error || 'Update failed');
  };

  const handleOrderStatusChange = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast.success('Order status updated');
      fetchDashboardData();
    } catch (err) { toast.error('Update failed'); }
  };

  const handleUserUpdate = async (targetUser, updates) => {
    try {
      await api.put(`/users/${targetUser._id}`, updates);
      toast.success('User updated');
      fetchDashboardData();
    } catch (err) { toast.error('Update failed'); }
  };

  const addNavItem = async () => {
    if (!newNavItem) return toast.error('Please enter a name');
    if (navItems.some(item => item.name === newNavItem)) return toast.error('Item already exists');
    try {
      const res = await api.post('/nav-items', { name: newNavItem, isPink: newNavItemPink, isBold: newNavItemBold });
      setNavItems(prev => [...prev, res]);
      setNewNavItem('');
      setNewNavItemPink(false);
      setNewNavItemBold(false);
      toast.success('Navigation item added');
    } catch (err) { toast.error('Failed to add item'); }
  };

  const removeNavItem = async (id) => {
    if (!window.confirm('Remove this navigation item?')) return;
    try {
      await api.delete(`/nav-items/${id}`);
      setNavItems(prev => prev.filter(item => item._id !== id));
      toast.success('Navigation item removed');
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleMegaMenuChange = (e) => setMegaMenuForm({ ...megaMenuForm, [e.target.name]: e.target.value });
  const addColumn = () => setMegaMenuForm(prev => ({ ...prev, columns: [...prev.columns, { ...emptyColumn }] }));
  const updateColumn = (index, field, value) => setMegaMenuForm(prev => {
    const updated = [...prev.columns];
    updated[index] = { ...updated[index], [field]: value };
    return { ...prev, columns: updated };
  });
  const removeColumn = (index) => setMegaMenuForm(prev => ({ ...prev, columns: prev.columns.filter((_, i) => i !== index) }));
  const addItemToColumn = (colIndex) => setMegaMenuForm(prev => {
    const updated = [...prev.columns];
    updated[colIndex].items = [...updated[colIndex].items, { ...emptyItem }];
    return { ...prev, columns: updated };
  });
  const updateItemInColumn = (colIndex, itemIndex, categoryName) => setMegaMenuForm(prev => {
    const updated = [...prev.columns];
    const selected = categories.find(c => c.name === categoryName);
    updated[colIndex].items[itemIndex] = {
      name: categoryName,
      link: selected ? `/category/${selected.slug}` : ''
    };
    return { ...prev, columns: updated };
  });
  const removeItemFromColumn = (colIndex, itemIndex) => setMegaMenuForm(prev => {
    const updated = [...prev.columns];
    updated[colIndex].items = updated[colIndex].items.filter((_, i) => i !== itemIndex);
    return { ...prev, columns: updated };
  });
  const handleMegaMenuSubmit = async (e) => {
    e.preventDefault();
    if (!megaMenuForm.navItem || megaMenuForm.columns.length === 0) return toast.error('Select nav item and add at least one column');
    setMegaMenuLoading(true);
    try {
      const payload = {
        navItem: megaMenuForm.navItem,
        columns: megaMenuForm.columns.map(col => ({
          ...col,
          items: col.items.filter(item => item.name).map(item => ({
            name: item.name,
            link: item.link || `/category/${categories.find(c => c.name === item.name)?.slug || ''}`
          }))
        }))
      };
      if (editingMegaMenuId) await api.put(`/megamenu/${editingMegaMenuId}`, payload);
      else await api.post('/megamenu', payload);
      toast.success(editingMegaMenuId ? 'Mega menu updated' : 'Mega menu added');
      setMegaMenuForm(emptyMegaMenuForm);
      setEditingMegaMenuId(null);
      fetchDashboardData();
    } catch (err) { toast.error('Save failed'); } finally { setMegaMenuLoading(false); }
  };
  const handleEditMegaMenu = (menu) => {
    setEditingMegaMenuId(menu._id);
    setMegaMenuForm({ navItem: menu.navItem, columns: menu.columns || [] });
    setActiveTab('megamenus');
  };
  const handleDeleteMegaMenu = async (id) => {
    if (!window.confirm('Delete this mega menu?')) return;
    try {
      await api.delete(`/megamenu/${id}`);
      toast.success('Mega menu deleted');
      fetchDashboardData();
    } catch (err) { toast.error('Delete failed'); }
  };

  const handleCardPaymentToggle = (e) => {
    const value = e.target.checked;
    setShowCardPayment(value);
    localStorage.setItem('showCardPayment', String(value));
    toast.success(`Card payment option ${value ? 'shown' : 'hidden'}`);
  };

  const formatPrice = (amount) => `Rs ${Number(amount || 0).toLocaleString()}`;
  const getOrderStatus = (order) => order.orderStatus || order.status || 'pending';

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-[#22c55e] text-white';
      case 'shipped': return 'bg-[#3b82f6] text-white';
      case 'out_for_delivery': return 'bg-[#8b5cf6] text-white';
      case 'processing': return 'bg-[#f59e0b] text-white';
      case 'confirmed': return 'bg-[#06b6d4] text-white';
      case 'cancelled': return 'bg-[#ef4444] text-white';
      case 'pending': return 'bg-[#9ca3af] text-white';
      default: return 'bg-[#9ca3af] text-white';
    }
  };

  if (loading) return <div className="min-h-[80vh] flex items-center justify-center"><div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div></div>;
  if (error) return <div className="min-h-[80vh] flex items-center justify-center text-red-600">{error}</div>;

  return (
    <div className="bg-white min-h-[90vh]">
      <div className="max-w-[1440px] mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-widest text-gray-500">{isFullAdmin ? 'Admin Dashboard' : (isAdminOrSubAdmin ? 'Sub-Admin Dashboard' : 'Customer Dashboard')}</p>
            <h1 className="text-3xl font-normal text-[#1c1d1d] mt-1">Welcome, {user?.name || 'User'}</h1>
            <p className="text-gray-500 mt-1 flex items-center gap-2">{user?.email}</p>
          </div>
          <button onClick={logout} className="text-white px-8 py-3 text-sm uppercase tracking-widest rounded hover:opacity-80 transition-colors" style={{ backgroundColor: theme.primaryColor }}>Logout</button>
        </div>
        
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden sticky top-4">
              <div className="bg-[#1c1d1d] px-6 py-4"><h3 className="text-white text-sm uppercase tracking-widest font-medium">Navigation</h3></div>
              <div className="p-2">
                {tabs.map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors rounded-lg ${activeTab === tab.key ? 'text-white' : 'text-gray-700 hover:bg-gray-50'}`} style={{ backgroundColor: activeTab === tab.key ? theme.primaryColor : 'transparent' }}>
                    <i className={`${tab.icon} w-5 text-center`}></i> {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex-1">
            {activeTab === 'overview' && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                  {isAdminOrSubAdmin && (
                    <>
                      <div className="bg-white rounded-xl shadow-sm border p-6"><p className="text-gray-500">Total Products</p><h3 className="text-3xl">{products.length}</h3></div>
                      <div className="bg-white rounded-xl shadow-sm border p-6"><p className="text-gray-500">Total Categories</p><h3 className="text-3xl">{categories.length}</h3></div>
                      {isFullAdmin && <div className="bg-white rounded-xl shadow-sm border p-6"><p className="text-gray-500">Total Users</p><h3 className="text-3xl">{users.length}</h3></div>}
                    </>
                  )}
                  <div className="bg-white rounded-xl shadow-sm border p-6"><p className="text-gray-500">{isAdminOrSubAdmin ? 'Total Orders' : 'My Orders'}</p><h3 className="text-3xl">{orders.length}</h3></div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h2 className="text-lg font-normal mb-4">Recent Orders</h2>
                  {orders.length === 0 ? (
                    <p className="text-gray-500">No orders yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="p-3 text-left">Order Number</th>
                            <th className="p-3 text-left">Status</th>
                            <th className="p-3 text-left">Total</th>
                            <th className="p-3 text-left">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.slice(0, 8).map(order => (
                            <tr key={order._id} className="border-b">
                              <td className="p-3">{order.orderNumber}</td>
                              <td className="p-3">{getOrderStatus(order)}</td>
                              <td className="p-3">{formatPrice(order.total)}</td>
                              <td className="p-3">{new Date(order.createdAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'orders' && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-normal mb-4">{isAdminOrSubAdmin ? 'Order Management' : 'My Order History'}</h2>
                {orders.length === 0 ? (
                  <p className="text-gray-500">No orders found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-gray-50 text-gray-600 text-sm border-b">
                          <th className="p-3 font-medium">Order / Date</th>
                          {isAdminOrSubAdmin && <th className="p-3 font-medium">Customer Details</th>}
                          <th className="p-3 font-medium">Items / Total</th>
                          <th className="p-3 font-medium">Status</th>
                          <th className="p-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map(order => (
                          <React.Fragment key={order._id}>
                            <tr className="border-b hover:bg-gray-50 transition-colors">
                              <td className="p-3">
                                <p className="font-semibold text-gray-900 text-sm">{order.orderNumber}</p>
                                <p className="text-xs text-gray-500 mt-1">{new Date(order.createdAt).toLocaleString()}</p>
                              </td>
                              {isAdminOrSubAdmin && (
                                <td className="p-3">
                                  <div className="space-y-1">
                                    <p className="font-medium text-gray-800 text-sm">{order.shippingAddress?.name || order.user?.name || 'Guest'}</p>
                                    <p className="text-xs text-gray-600">{order.shippingAddress?.email || order.user?.email}</p>
                                    <p className="text-xs text-gray-600">{order.shippingAddress?.phone}</p>
                                    <p className="text-xs text-gray-600">
                                      {order.shippingAddress?.address}, {order.shippingAddress?.city}, {order.shippingAddress?.province}
                                      {order.shippingAddress?.postalCode ? ` - ${order.shippingAddress.postalCode}` : ''}
                                    </p>
                                  </div>
                                </td>
                              )}
                              <td className="p-3">
                                <p className="text-sm font-medium text-gray-900">{formatPrice(order.total)}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{order.items?.length || 0} Product(s)</p>
                                <button 
                                  onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                                  className="text-xs text-blue-600 hover:underline mt-1"
                                >
                                  {expandedOrder === order._id ? 'Hide Details' : 'View Details'}
                                </button>
                              </td>
                              <td className="p-3">
                                {isAdminOrSubAdmin ? (
                                  <select 
                                    value={getOrderStatus(order)} 
                                    onChange={(e) => handleOrderStatusChange(order._id, e.target.value)} 
                                    className="border border-gray-300 rounded p-2 text-sm font-medium outline-none bg-white"
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="processing">Processing</option>
                                    <option value="shipped">Shipped</option>
                                    <option value="out_for_delivery">Out for Delivery</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="cancelled">Cancelled</option>
                                  </select>
                                ) : (
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusColor(getOrderStatus(order))}`}>
                                    {getOrderStatus(order).replace(/_/g, ' ')}
                                  </span>
                                )}
                              </td>
                              <td className="p-3">
                                {!isAdminOrSubAdmin && canCancel(order) && (
                                  <button
                                    onClick={() => handleCancelOrder(order._id)}
                                    disabled={cancellingOrderId === order._id}
                                    className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {cancellingOrderId === order._id ? 'Cancelling...' : 'Cancel'}
                                  </button>
                                )}
                              </td>
                            </tr>
                            {expandedOrder === order._id && (
                              <tr>
                                <td colSpan={isAdminOrSubAdmin ? 5 : 4} className="p-4 bg-gray-50 border-b">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Order Items</h4>
                                      <div className="space-y-2">
                                        {order.items?.map((item, idx) => (
                                          <div key={idx} className="flex items-center gap-3 border-b border-gray-200 pb-2">
                                            <img 
                                              src={item.productImage || 'https://via.placeholder.com/50'} 
                                              alt={item.productName} 
                                              className="w-12 h-12 object-cover rounded border" 
                                            />
                                            <div className="flex-1">
                                              <p className="text-sm font-medium text-gray-900">{item.productName}</p>
                                              <p className="text-xs text-gray-500">
                                                Qty: {item.quantity} 
                                                {item.color && <span className="ml-2">Color: <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span> {item.color}</span>}
                                                {item.size && <span className="ml-2">Size: {item.size}</span>}
                                              </p>
                                            </div>
                                            <span className="text-sm text-gray-900">Rs. {(item.price * item.quantity).toLocaleString()}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div>
                                      {isAdminOrSubAdmin && (
                                        <>
                                          <h4 className="text-sm font-semibold text-gray-700 mb-1">Shipping Address</h4>
                                          <p className="text-sm text-gray-600 mb-4">
                                            {order.shippingAddress?.name}<br />
                                            {order.shippingAddress?.address}<br />
                                            {order.shippingAddress?.city}, {order.shippingAddress?.province}<br />
                                            Phone: {order.shippingAddress?.phone}
                                          </p>
                                          {order.billingAddress && (
                                            <>
                                              <h4 className="text-sm font-semibold text-gray-700 mb-1">Billing Address (Different)</h4>
                                              <p className="text-sm text-gray-600 mb-4">
                                                {order.billingAddress?.name}<br />
                                                {order.billingAddress?.address}<br />
                                                {order.billingAddress?.city}, {order.billingAddress?.province}<br />
                                                Phone: {order.billingAddress?.phone}
                                              </p>
                                            </>
                                          )}
                                          <h4 className="text-sm font-semibold text-gray-700 mb-1">Payment Method</h4>
                                          <p className="text-sm text-gray-600 mb-4 capitalize">{order.paymentMethod}</p>
                                          {order.paymentMethod === 'banktransfer' && order.receiptImage && (
                                            <div className="mt-2">
                                              <p className="text-sm font-medium text-gray-700 mb-1">Payment Receipt</p>
                                              <a href={order.receiptImage} target="_blank" rel="noopener noreferrer">
                                                <img src={order.receiptImage} alt="Payment Receipt" className="max-w-full h-auto max-h-48 object-contain border rounded" />
                                              </a>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      <h4 className="text-sm font-semibold text-gray-700 mb-1">Order Summary</h4>
                                      <div className="space-y-1 text-sm">
                                        <div className="flex justify-between"><span>Subtotal</span><span>Rs. {order.subtotal?.toLocaleString()}</span></div>
                                        {order.discount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>-Rs. {order.discount.toLocaleString()}</span></div>}
                                        <div className="flex justify-between"><span>Shipping</span><span>Rs. {order.shippingCost?.toLocaleString()}</span></div>
                                        {order.tax > 0 && <div className="flex justify-between"><span>Tax</span><span>Rs. {order.tax?.toLocaleString()}</span></div>}
                                        <div className="flex justify-between font-semibold border-t pt-1"><span>Total</span><span>Rs. {order.total?.toLocaleString()}</span></div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'products' && isAdminOrSubAdmin && (
              <div>
                <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                  <h2 className="text-lg font-normal mb-4">{editingProductId ? 'Update Product' : 'Add New Product'}</h2>
                  <form onSubmit={handleProductSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input name="name" value={productForm.name} onChange={handleProductChange} placeholder="Product Name" className="border p-3 rounded" required />
                    <input name="slug" value={productForm.slug} onChange={handleProductChange} placeholder="Slug" className="border p-3 rounded" />
                    <input name="price" type="number" value={productForm.price} onChange={handleProductChange} placeholder="Price" className="border p-3 rounded" required />
                    <input name="originalPrice" type="number" value={productForm.originalPrice} onChange={handleProductChange} placeholder="Original Price" className="border p-3 rounded" />
                    <select name="category" value={productForm.category} onChange={handleProductChange} className="border p-3 rounded bg-white" required>
                      <option value="">Select Category</option>
                      {categories.map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
                    </select>
                    <input name="brand" value={productForm.brand} onChange={handleProductChange} placeholder="Brand" className="border p-3 rounded" />
                    <input name="sizes" value={productForm.sizes} onChange={handleProductChange} placeholder="Sizes (comma: 36,37,38)" className="border p-3 rounded" />
                    <input name="colors" value={productForm.colors} onChange={handleProductChange} placeholder="Colors (comma: Black,Red)" className="border p-3 rounded" />
                    <input name="stockQuantity" type="number" value={productForm.stockQuantity} onChange={handleProductChange} placeholder="Stock" className="border p-3 rounded" />
                    <input name="leftBadge" value={productForm.leftBadge} onChange={handleProductChange} placeholder="Left Badge" className="border p-3 rounded" />
                    <input name="rightBadge" value={productForm.rightBadge} onChange={handleProductChange} placeholder="Right Badge" className="border p-3 rounded" />
                    <textarea name="shortDescription" value={productForm.shortDescription} onChange={handleProductChange} placeholder="Short Description" className="border p-3 rounded md:col-span-2" />
                    <textarea name="description" value={productForm.description} onChange={handleProductChange} placeholder="Description" className="border p-3 rounded md:col-span-2" rows="3" />

                    {/* Variants Table */}
                    <div className="md:col-span-2">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-medium">Variants</h3>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={generateVariantsFromSizesColors}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            Generate from Sizes/Colors
                          </button>
                          <button
                            type="button"
                            onClick={addVariantRow}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                          >
                            + Add Variant
                          </button>
                        </div>
                      </div>
                      {productForm.variants.length === 0 ? (
                        <p className="text-gray-500 text-sm">No variants added. Add manually or generate from sizes/colors.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-gray-300">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border p-2 text-left">Size</th>
                                <th className="border p-2 text-left">Color</th>
                                <th className="border p-2 text-left">Quantity</th>
                                <th className="border p-2 text-left">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {productForm.variants.map((variant, index) => (
                                <tr key={index}>
                                  <td className="border p-2">
                                    <input
                                      type="text"
                                      value={variant.size}
                                      onChange={(e) => handleProductVariantChange(index, 'size', e.target.value)}
                                      placeholder="e.g. M"
                                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                    />
                                  </td>
                                  <td className="border p-2">
                                    <input
                                      type="text"
                                      value={variant.color}
                                      onChange={(e) => handleProductVariantChange(index, 'color', e.target.value)}
                                      placeholder="e.g. Red"
                                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                    />
                                  </td>
                                  <td className="border p-2">
                                    <input
                                      type="number"
                                      min="0"
                                      value={variant.quantity}
                                      onChange={(e) => handleProductVariantChange(index, 'quantity', parseInt(e.target.value) || 0)}
                                      className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                                    />
                                  </td>
                                  <td className="border p-2">
                                    <button
                                      type="button"
                                      onClick={() => removeVariantRow(index)}
                                      className="text-red-600 hover:text-red-800 text-sm"
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">Each variant represents a specific size/color combination with its own stock quantity.</p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">Product Images (Max 20)</label>
                      <input type="file" multiple accept="image/*" onChange={handleProductImageSelect} className="border p-2 rounded w-full" />
                      <div className="text-xs text-gray-500 mt-1">{productImageFiles.length} / 20 images selected</div>
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {productForm.images.map((img, idx) => (
                          <div key={idx} className="relative">
                            <img src={img} alt="preview" className="w-full h-20 object-cover border rounded" />
                            <button type="button" onClick={() => removeProductImage(idx)} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 text-xs flex justify-center items-center">x</button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="md:col-span-2 flex gap-4">
                      <label className="flex items-center gap-2"><input type="checkbox" name="isFeatured" checked={productForm.isFeatured} onChange={handleProductChange} /> Featured</label>
                      <label className="flex items-center gap-2"><input type="checkbox" name="isBestSeller" checked={productForm.isBestSeller} onChange={handleProductChange} /> Best Seller</label>
                      <label className="flex items-center gap-2"><input type="checkbox" name="isNew" checked={productForm.isNew} onChange={handleProductChange} /> New Arrival</label>
                    </div>
                    <div className="md:col-span-2 flex gap-4">
                      <button type="submit" disabled={uploadingProduct} className="text-white px-6 py-3 rounded hover:opacity-80 transition-colors" style={{ backgroundColor: theme.primaryColor }}>{uploadingProduct ? 'Saving...' : (editingProductId ? 'Update Product' : 'Add Product')}</button>
                      {editingProductId && <button type="button" onClick={() => { setEditingProductId(null); setProductForm(emptyProductForm); setProductImageFiles([]); }} className="border px-6 py-3 rounded">Cancel</button>}
                    </div>
                  </form>
                </div>
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h2 className="text-lg font-normal mb-4">All Products</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="p-3 text-left font-medium text-sm">Image</th>
                          <th className="p-3 text-left font-medium text-sm">Name</th>
                          <th className="p-3 text-left font-medium text-sm">Price</th>
                          <th className="p-3 text-left font-medium text-sm">Stock</th>
                          <th className="p-3 text-left font-medium text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map(p => (
                          <tr key={p._id} className="border-b">
                            <td className="p-3"><img src={p.images?.[0] || 'https://via.placeholder.com/50'} alt={p.name} className="w-12 h-12 object-cover rounded" /></td>
                            <td className="p-3 text-sm">{p.name}</td>
                            <td className="p-3 text-sm">{formatPrice(p.price)}</td>
                            <td className="p-3 text-sm">{p.stockQuantity}</td>
                            <td className="p-3">
                              <button onClick={() => handleEditProduct(p)} className="bg-blue-600 text-white px-3 py-1 rounded text-sm mr-2">Edit</button>
                              <button onClick={() => handleDeleteProduct(p._id)} className="bg-red-600 text-white px-3 py-1 rounded text-sm">Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'categories' && isAdminOrSubAdmin && (
              <div>
                <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                  <h2 className="text-lg font-normal mb-4">{editingCategoryId ? 'Update Category' : 'Add New Category'}</h2>
                  <form onSubmit={handleCategorySubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input name="name" value={categoryForm.name} onChange={handleCategoryChange} placeholder="Category Name" className="border p-3 rounded" required />
                    <input name="slug" value={categoryForm.slug} onChange={handleCategoryChange} placeholder="Slug" className="border p-3 rounded" required />
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Category Image</label>
                      <input type="file" accept="image/*" onChange={handleCategoryImageSelect} className="border p-2 rounded w-full" />
                      {categoryForm.image && <div className="mt-2"><img src={categoryForm.image} alt="preview" className="w-32 h-32 object-cover rounded border" /></div>}
                    </div>
                    <textarea name="description" value={categoryForm.description} onChange={handleCategoryChange} placeholder="Description" className="border p-3 rounded md:col-span-2" />
                    <div className="md:col-span-2 flex gap-4">
                      <button type="submit" disabled={uploadingCategory} className="text-white px-6 py-3 rounded hover:opacity-80 transition-colors" style={{ backgroundColor: theme.primaryColor }}>{uploadingCategory ? 'Saving...' : (editingCategoryId ? 'Update Category' : 'Add Category')}</button>
                      {editingCategoryId && <button type="button" onClick={() => { setEditingCategoryId(null); setCategoryForm(emptyCategoryForm); setCategoryImageFile(null); }} className="border px-6 py-3 rounded">Cancel</button>}
                    </div>
                  </form>
                </div>
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h2 className="text-lg font-normal mb-4">All Categories</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="p-3 text-left font-medium text-sm">Image</th>
                          <th className="p-3 text-left font-medium text-sm">Name</th>
                          <th className="p-3 text-left font-medium text-sm">Slug</th>
                          <th className="p-3 text-left font-medium text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map(c => (
                          <tr key={c._id} className="border-b">
                            <td className="p-3"><img src={c.image || 'https://via.placeholder.com/50'} alt={c.name} className="w-12 h-12 object-cover rounded" /></td>
                            <td className="p-3 text-sm">{c.name}</td>
                            <td className="p-3 text-sm">{c.slug}</td>
                            <td className="p-3">
                              <button onClick={() => handleEditCategory(c)} className="bg-blue-600 text-white px-3 py-1 rounded text-sm mr-2">Edit</button>
                              <button onClick={() => handleDeleteCategory(c._id)} className="bg-red-600 text-white px-3 py-1 rounded text-sm">Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'blogs' && isAdminOrSubAdmin && (
              <div>
                <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                  <h2 className="text-lg font-normal mb-4">{editingBlogId ? 'Update Blog' : 'Create New Blog'}</h2>
                  <form onSubmit={handleBlogSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input name="title" value={blogForm.title} onChange={handleBlogChange} placeholder="Blog Title" className="border p-3 rounded w-full outline-none focus:border-[#d60167]" required />
                      <input name="slug" value={blogForm.slug} onChange={handleBlogChange} placeholder="Slug (auto-generated if empty)" className="border p-3 rounded w-full outline-none focus:border-[#d60167]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Featured Image</label>
                      <input type="file" accept="image/*" onChange={handleBlogImageUpload} disabled={uploadingBlogImage} className="border p-2 rounded w-full" />
                      {blogForm.image && <img src={blogForm.image} alt="preview" className="mt-2 h-32 object-cover rounded border" />}
                    </div>
                    <textarea name="excerpt" value={blogForm.excerpt} onChange={handleBlogChange} placeholder="Short excerpt (optional)" className="border p-3 rounded w-full outline-none focus:border-[#d60167]" rows="2" />
                    <textarea name="content" value={blogForm.content} onChange={handleBlogChange} placeholder="Blog Content" className="border p-3 rounded w-full outline-none focus:border-[#d60167]" rows="6" required />
                    <div>
                      <label className="block text-sm font-medium mb-1">Status</label>
                      <select name="status" value={blogForm.status} onChange={handleBlogChange} className="border p-3 rounded w-full outline-none bg-white">
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                    <div className="flex gap-4">
                      <button type="submit" className="text-white px-6 py-3 rounded hover:opacity-80 transition-colors" style={{ backgroundColor: theme.primaryColor }}>{editingBlogId ? 'Update Blog' : 'Create Blog'}</button>
                      {editingBlogId && <button type="button" onClick={() => { setEditingBlogId(null); setBlogForm({ title: '', slug: '', content: '', excerpt: '', image: '', status: 'draft' }); }} className="border border-gray-300 px-6 py-3 rounded hover:bg-gray-50">Cancel</button>}
                    </div>
                  </form>
                </div>

                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h2 className="text-lg font-normal mb-4">All Blogs</h2>
                  {blogs.length === 0 ? (
                    <p className="text-gray-500">No blogs yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b bg-gray-50 text-gray-600 text-sm">
                            <th className="p-3 font-medium">Image</th>
                            <th className="p-3 font-medium">Title</th>
                            <th className="p-3 font-medium">Status</th>
                            <th className="p-3 font-medium">Date</th>
                            <th className="p-3 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {blogs.map(blog => (
                            <tr key={blog._id} className="border-b">
                              <td className="p-3">{blog.image ? <img src={blog.image} alt={blog.title} className="w-12 h-12 object-cover rounded" /> : <div className="w-12 h-12 bg-gray-200 rounded"></div>}</td>
                              <td className="p-3 text-sm font-medium text-gray-800">{blog.title}</td>
                              <td className="p-3 text-sm"><span className={`px-2 py-1 rounded text-xs font-medium ${blog.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{blog.status}</span></td>
                              <td className="p-3 text-sm text-gray-600">{new Date(blog.createdAt).toLocaleDateString()}</td>
                              <td className="p-3">
                                <button onClick={() => handleEditBlog(blog)} className="bg-blue-600 text-white px-3 py-1 rounded text-sm mr-2">Edit</button>
                                <button onClick={() => handleDeleteBlog(blog._id)} className="bg-red-600 text-white px-3 py-1 rounded text-sm">Delete</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isFullAdmin && (
              <>
                {activeTab === 'heroImages' && (
                  <div>
                    <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                      <h2 className="text-lg font-normal mb-4">{editingHeroId ? 'Update Hero Image' : 'Add Hero Image'}</h2>
                      <form onSubmit={handleHeroSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Desktop Image</label>
                          <input type="file" accept="image/*" onChange={(e) => handleHeroImageUpload(e.target.files[0], false)} disabled={uploadingHero} className="border p-2 rounded w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Mobile Image (Optional)</label>
                          <input type="file" accept="image/*" onChange={(e) => handleHeroImageUpload(e.target.files[0], true)} disabled={uploadingHero} className="border p-2 rounded w-full" />
                        </div>
                        {heroForm.imageUrl && <div><img src={heroForm.imageUrl} alt="Desktop Preview" className="h-40 object-cover rounded border" /></div>}
                        {heroForm.mobileImageUrl && <div><img src={heroForm.mobileImageUrl} alt="Mobile Preview" className="h-40 object-cover rounded border" /></div>}
                        <input name="title" value={heroForm.title} onChange={handleHeroChange} placeholder="Title (optional)" className="border p-3 rounded w-full" />
                        <input name="link" value={heroForm.link} onChange={handleHeroChange} placeholder="Link URL" className="border p-3 rounded w-full" />
                        <input name="order" type="number" value={heroForm.order} onChange={handleHeroChange} placeholder="Display Order (Lower = First)" className="border p-3 rounded w-full" />
                        <div className="flex gap-4">
                          <button type="submit" className="text-white px-6 py-3 rounded hover:opacity-80 transition-colors" style={{ backgroundColor: theme.primaryColor }}>{editingHeroId ? 'Update Hero' : 'Add Hero'}</button>
                          {editingHeroId && <button type="button" onClick={() => { setEditingHeroId(null); setHeroForm(emptyHeroForm); }} className="border px-6 py-3 rounded">Cancel</button>}
                        </div>
                      </form>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <h2 className="text-lg font-normal mb-4">Existing Hero Images</h2>
                      {heroImages.length === 0 ? <p className="text-gray-500">No hero images</p> : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b bg-gray-50">
                                <th className="p-3 text-left font-medium text-sm">Desktop Image</th>
                                <th className="p-3 text-left font-medium text-sm">Mobile Image</th>
                                <th className="p-3 text-left font-medium text-sm">Title</th>
                                <th className="p-3 text-left font-medium text-sm">Link</th>
                                <th className="p-3 text-left font-medium text-sm">Order</th>
                                <th className="p-3 text-left font-medium text-sm">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {heroImages.sort((a,b) => a.order - b.order).map(h => (
                                <tr key={h._id} className="border-b">
                                  <td className="p-3"><img src={h.imageUrl} alt="desktop" className="w-20 h-20 object-cover rounded border" /></td>
                                  <td className="p-3">{h.mobileImageUrl ? <img src={h.mobileImageUrl} alt="mobile" className="w-20 h-20 object-cover rounded border" /> : '-'}</td>
                                  <td className="p-3 text-sm">{h.title || '-'}</td>
                                  <td className="p-3 text-sm">{h.link || '-'}</td>
                                  <td className="p-3 text-sm">{h.order ?? 0}</td>
                                  <td className="p-3">
                                    <button onClick={() => handleEditHero(h)} className="bg-blue-600 text-white px-3 py-1 rounded text-sm mr-2">Edit</button>
                                    <button onClick={() => handleDeleteHero(h._id)} className="bg-red-600 text-white px-3 py-1 rounded text-sm">Delete</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'megamenus' && (
                  <div>
                    <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                      <h2 className="text-lg font-normal mb-4">Manage Navigation Items</h2>
                      <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded border">
                        {navItems.map(item => (
                          <div key={item._id} className="bg-white border rounded px-3 py-1 flex items-center gap-2">
                            <span className={`text-sm ${item.isPink ? 'text-[#d60167]' : 'text-gray-700'} ${item.isBold ? 'font-bold' : ''}`}>{item.name}</span>
                            <button onClick={() => removeNavItem(item._id)} className="text-red-500 font-bold hover:text-red-700">×</button>
                          </div>
                        ))}
                      </div>
                      <div className="flex flex-col gap-3 max-w-md">
                        <input value={newNavItem} onChange={(e) => setNewNavItem(e.target.value)} placeholder="New navigation item name" className="border p-3 rounded outline-none focus:border-[#d60167]" />
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-1 cursor-pointer text-sm"><input type="checkbox" checked={newNavItemPink} onChange={(e) => setNewNavItemPink(e.target.checked)} className="w-4 h-4" /> Pink Color</label>
                          <label className="flex items-center gap-1 cursor-pointer text-sm"><input type="checkbox" checked={newNavItemBold} onChange={(e) => setNewNavItemBold(e.target.checked)} className="w-4 h-4" /> Bold Text</label>
                          <button onClick={addNavItem} className="text-white px-4 py-2 rounded hover:opacity-80 transition-colors text-sm" style={{ backgroundColor: theme.primaryColor }}>Add Item</button>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                      <h2 className="text-lg font-normal mb-4">{editingMegaMenuId ? 'Update Mega Menu Dropdown' : 'Build Mega Menu Dropdown'}</h2>
                      <form onSubmit={handleMegaMenuSubmit} className="space-y-6">
                        <select name="navItem" value={megaMenuForm.navItem} onChange={handleMegaMenuChange} className="border p-3 rounded w-full outline-none focus:border-[#d60167] bg-white" required>
                          <option value="" disabled>Select Navigation Item</option>
                          {navItems.map(item => <option key={item._id} value={item.name}>{item.name}</option>)}
                        </select>
                        
                        <div className="border-t pt-4">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="font-medium text-gray-800">Dropdown Columns</h3>
                            <button type="button" onClick={addColumn} className="bg-[#1c1d1d] text-white px-3 py-1.5 rounded text-sm hover:bg-black transition-colors">+ Add Column</button>
                          </div>
                          
                          {megaMenuForm.columns.length === 0 && <p className="text-gray-500 text-sm italic">No columns added yet.</p>}
                          
                          {megaMenuForm.columns.map((col, ci) => (
                            <div key={ci} className="bg-gray-50 p-4 rounded mb-4 border border-gray-200">
                              <div className="flex justify-between mb-3">
                                <h4 className="font-medium text-gray-700">Column {ci+1}</h4>
                                <button type="button" onClick={() => removeColumn(ci)} className="text-red-600 text-sm hover:text-red-800 font-medium">Remove Column</button>
                              </div>
                              
                              <input value={col.title} onChange={(e) => updateColumn(ci, 'title', e.target.value)} placeholder="Column Heading (e.g. MEN)" className="border p-2 rounded w-full mb-3 outline-none focus:border-[#d60167]" required />
                              
                              <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-700 p-2 bg-white rounded border">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input type="checkbox" checked={col.shopAll} onChange={(e) => updateColumn(ci, 'shopAll', e.target.checked)} className="w-4 h-4" /> Show "Shop All" link
                                </label>
                                {col.shopAll && <input value={col.shopAllLink} onChange={(e) => updateColumn(ci, 'shopAllLink', e.target.value)} placeholder="Custom Link (Optional)" className="border p-1.5 rounded outline-none text-sm" />}
                                <label className="flex items-center gap-1.5 cursor-pointer ml-2">
                                  <input type="checkbox" checked={col.highlightFirst} onChange={(e) => updateColumn(ci, 'highlightFirst', e.target.checked)} className="w-4 h-4" /> Highlight First Item
                                </label>
                              </div>
                              
                              <div className="bg-white p-3 rounded border border-gray-200">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-sm font-medium text-gray-700">Linked Categories ({col.items.length})</span>
                                  <button type="button" onClick={() => addItemToColumn(ci)} className="bg-gray-800 text-white px-2 py-1 rounded text-xs hover:bg-black transition-colors">+ Add Category</button>
                                </div>
                                {col.items.length === 0 && <p className="text-xs text-gray-400 italic mb-1">No categories added.</p>}
                                {col.items.map((item, ii) => (
                                  <div key={ii} className="flex gap-2 mt-2">
                                    <select
                                      value={item.name || ''}
                                      onChange={(e) => updateItemInColumn(ci, ii, e.target.value)}
                                      className="border p-2 rounded flex-1 text-sm outline-none bg-gray-50"
                                      required
                                    >
                                      <option value="" disabled>Select Category from Database</option>
                                      {categories.map(cat => (
                                        <option key={cat._id} value={cat.name}>{cat.name}</option>
                                      ))}
                                    </select>
                                    <button type="button" onClick={() => removeItemFromColumn(ci, ii)} className="text-red-500 px-3 border border-red-200 rounded hover:bg-red-50 transition-colors font-bold">×</button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                          
                          <div className="flex gap-4 mt-6">
                            <button type="submit" disabled={megaMenuLoading} className="text-white px-6 py-2.5 rounded font-medium hover:opacity-80 transition-colors" style={{ backgroundColor: theme.primaryColor }}>{megaMenuLoading ? 'Saving...' : (editingMegaMenuId ? 'Update Mega Menu' : 'Save Mega Menu')}</button>
                            {editingMegaMenuId && <button type="button" onClick={() => { setEditingMegaMenuId(null); setMegaMenuForm(emptyMegaMenuForm); }} className="border border-gray-300 text-gray-700 px-6 py-2.5 rounded hover:bg-gray-50 transition-colors">Cancel Edit</button>}
                          </div>
                        </div>
                      </form>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <h2 className="text-lg font-normal mb-4">Configured Mega Menus</h2>
                      {megaMenus.length === 0 ? <p className="text-gray-500">No mega menus configured yet.</p> : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left">
                            <thead>
                              <tr className="border-b bg-gray-50 text-gray-600 text-sm">
                                <th className="p-3 font-medium">Nav Item</th>
                                <th className="p-3 font-medium">Columns Setup</th>
                                <th className="p-3 font-medium">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {megaMenus.map(m => (
                                <tr key={m._id} className="border-b">
                                  <td className="p-3 font-semibold text-gray-800 text-sm">{m.navItem}</td>
                                  <td className="p-3 text-gray-600 text-sm">
                                    {m.columns?.length || 0} Columns ({m.columns?.reduce((acc, col) => acc + (col.items?.length || 0), 0) || 0} categories)
                                  </td>
                                  <td className="p-3">
                                    <button onClick={() => handleEditMegaMenu(m)} className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors mr-2">Edit</button>
                                    <button onClick={() => handleDeleteMegaMenu(m._id)} className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors">Delete</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'sections' && (
                  <div className="animate-fade-in">
                    <SectionManager />
                  </div>
                )}

                {activeTab === 'coupons' && (
                  <div>
                    <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                      <h2 className="text-lg font-normal mb-4">Create New Coupon</h2>
                      <form onSubmit={handleCouponSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Coupon Code *</label>
                          <input name="code" value={couponForm.code} onChange={handleCouponChange} placeholder="e.g. SUMMER20" className="border p-3 rounded w-full outline-none uppercase" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Discount Type *</label>
                          <select name="discountType" value={couponForm.discountType} onChange={handleCouponChange} className="border p-3 rounded w-full outline-none bg-white">
                            <option value="percentage">Percentage (%)</option>
                            <option value="fixed">Fixed Amount (Rs)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Discount Value *</label>
                          <input name="discountValue" type="number" value={couponForm.discountValue} onChange={handleCouponChange} placeholder="e.g. 20 or 500" className="border p-3 rounded w-full outline-none" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Minimum Cart Amount (Optional)</label>
                          <input name="minCartAmount" type="number" value={couponForm.minCartAmount} onChange={handleCouponChange} placeholder="e.g. 3500" className="border p-3 rounded w-full outline-none" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Expiry Date (Optional)</label>
                          <input name="expiryDate" type="date" value={couponForm.expiryDate} onChange={handleCouponChange} className="border p-3 rounded w-full outline-none" />
                        </div>
                        <div className="md:col-span-2 mt-2">
                          <button type="submit" disabled={uploadingCoupon} className="text-white px-6 py-3 rounded hover:opacity-80 transition-colors" style={{ backgroundColor: theme.primaryColor }}>
                            {uploadingCoupon ? 'Creating...' : 'Create Coupon'}
                          </button>
                        </div>
                      </form>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <h2 className="text-lg font-normal mb-4">Active Coupons</h2>
                      {coupons.length === 0 ? <p className="text-gray-500">No coupons active.</p> : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left">
                            <thead>
                              <tr className="border-b bg-gray-50 text-gray-600 text-sm">
                                <th className="p-3 font-medium">Code</th>
                                <th className="p-3 font-medium">Type</th>
                                <th className="p-3 font-medium">Value</th>
                                <th className="p-3 font-medium">Min. Order</th>
                                <th className="p-3 font-medium">Expiry</th>
                                <th className="p-3 font-medium">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {coupons.map(c => (
                                <tr key={c._id} className="border-b">
                                  <td className="p-3 font-bold text-gray-800">{c.code}</td>
                                  <td className="p-3 text-sm capitalize">{c.discountType}</td>
                                  <td className="p-3 text-sm">{c.discountType === 'percentage' ? `${c.discountValue}%` : `Rs. ${c.discountValue}`}</td>
                                  <td className="p-3 text-sm">Rs. {c.minCartAmount || 0}</td>
                                  <td className="p-3 text-sm">{c.expiryDate ? new Date(c.expiryDate).toLocaleDateString() : 'Never'}</td>
                                  <td className="p-3 text-sm">
                                    <button onClick={() => handleDeleteCoupon(c._id)} className="bg-red-600 text-white px-3 py-1 rounded">Delete</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'footer' && (
                  <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                    <h2 className="text-xl font-normal mb-6">Footer Settings</h2>
                    
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-medium mb-3">Footer Columns</h3>
                        <div className="space-y-4">
                          {footerData.columns.map((col, colIndex) => (
                            <div key={colIndex} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                              <div className="flex justify-between items-center mb-3">
                                <h4 className="font-medium text-gray-700">Column {colIndex + 1}</h4>
                                <button onClick={() => handleRemoveColumn(colIndex)} className="text-red-600 text-sm hover:text-red-800">Remove Column</button>
                              </div>
                              
                              <input 
                                type="text" 
                                value={col.title} 
                                onChange={(e) => {
                                  const newData = { ...footerData };
                                  newData.columns[colIndex].title = e.target.value;
                                  setFooterData(newData);
                                }} 
                                placeholder="Column Title (e.g. INFORMATION)" 
                                className="w-full border border-gray-300 rounded p-2 mb-3 text-sm"
                              />
                              
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-gray-600 mb-1">Links in this column</p>
                                {col.items.map((item, itemIndex) => (
                                  <div key={itemIndex} className="flex gap-2">
                                    <input 
                                      type="text" 
                                      value={item.name} 
                                      onChange={(e) => {
                                        const newData = { ...footerData };
                                        newData.columns[colIndex].items[itemIndex].name = e.target.value;
                                        setFooterData(newData);
                                      }} 
                                      placeholder="Link Name" 
                                      className="flex-1 border border-gray-300 rounded p-2 text-sm"
                                    />
                                    <input 
                                      type="text" 
                                      value={item.link} 
                                      onChange={(e) => {
                                        const newData = { ...footerData };
                                        newData.columns[colIndex].items[itemIndex].link = e.target.value;
                                        setFooterData(newData);
                                      }} 
                                      placeholder="URL (e.g. /contact)" 
                                      className="flex-1 border border-gray-300 rounded p-2 text-sm"
                                    />
                                    <button onClick={() => handleRemoveItemFromColumn(colIndex, itemIndex)} className="text-red-600 font-bold hover:text-red-800 px-2">×</button>
                                  </div>
                                ))}
                                <button onClick={() => handleAddItemToColumn(colIndex)} className="text-blue-600 text-sm hover:underline">+ Add Link</button>
                              </div>
                            </div>
                          ))}
                          <button onClick={handleAddColumn} className="bg-[#1c1d1d] text-white px-4 py-2 rounded text-sm hover:bg-black transition-colors">+ Add Column</button>
                        </div>
                      </div>

                      <div className="border-t pt-6">
                        <h3 className="text-lg font-medium mb-3">Bottom Links</h3>
                        <div className="space-y-2">
                          {footerData.bottomLinks.map((link, index) => (
                            <div key={index} className="flex gap-2">
                              <input 
                                type="text" 
                                value={link.name} 
                                onChange={(e) => {
                                  const newData = { ...footerData };
                                  newData.bottomLinks[index].name = e.target.value;
                                  setFooterData(newData);
                                }} 
                                placeholder="Link Name" 
                                className="flex-1 border border-gray-300 rounded p-2 text-sm"
                              />
                              <input 
                                type="text" 
                                value={link.link} 
                                onChange={(e) => {
                                  const newData = { ...footerData };
                                  newData.bottomLinks[index].link = e.target.value;
                                  setFooterData(newData);
                                }} 
                                placeholder="URL (e.g. /sitemap)" 
                                className="flex-1 border border-gray-300 rounded p-2 text-sm"
                              />
                              <button onClick={() => {
                                const newData = { ...footerData };
                                newData.bottomLinks.splice(index, 1);
                                setFooterData(newData);
                              }} className="text-red-600 font-bold hover:text-red-800 px-2">×</button>
                            </div>
                          ))}
                          <button onClick={() => {
                            setFooterData(prev => ({
                              ...prev,
                              bottomLinks: [...prev.bottomLinks, { name: '', link: '' }]
                            }));
                          }} className="text-blue-600 text-sm hover:underline">+ Add Bottom Link</button>
                        </div>
                      </div>

                      <div className="border-t pt-6">
                        <h3 className="text-lg font-medium mb-3">Social Media Links</h3>
                        <p className="text-sm text-gray-500 mb-3">Available Icons: FaFacebookF, FaInstagram, FaYoutube, FaXTwitter, FaPinterest, FaLinkedinIn</p>
                        <div className="space-y-2">
                          {footerData.socialLinks.map((social, index) => (
                            <div key={index} className="flex gap-2">
                              <input 
                                type="text" 
                                value={social.name} 
                                onChange={(e) => {
                                  const newData = { ...footerData };
                                  newData.socialLinks[index].name = e.target.value;
                                  setFooterData(newData);
                                }} 
                                placeholder="Name (e.g. Facebook)" 
                                className="flex-1 border border-gray-300 rounded p-2 text-sm"
                              />
                              <input 
                                type="text" 
                                value={social.icon} 
                                onChange={(e) => {
                                  const newData = { ...footerData };
                                  newData.socialLinks[index].icon = e.target.value;
                                  setFooterData(newData);
                                }} 
                                placeholder="Icon Name (e.g. FaFacebookF)" 
                                className="flex-1 border border-gray-300 rounded p-2 text-sm"
                              />
                              <input 
                                type="text" 
                                value={social.link} 
                                onChange={(e) => {
                                  const newData = { ...footerData };
                                  newData.socialLinks[index].link = e.target.value;
                                  setFooterData(newData);
                                }} 
                                placeholder="URL" 
                                className="flex-1 border border-gray-300 rounded p-2 text-sm"
                              />
                              <button onClick={() => {
                                const newData = { ...footerData };
                                newData.socialLinks.splice(index, 1);
                                setFooterData(newData);
                              }} className="text-red-600 font-bold hover:text-red-800 px-2">×</button>
                            </div>
                          ))}
                          <button onClick={() => {
                            setFooterData(prev => ({
                              ...prev,
                              socialLinks: [...prev.socialLinks, { name: '', icon: '', link: '' }]
                            }));
                          }} className="text-blue-600 text-sm hover:underline">+ Add Social Link</button>
                        </div>
                      </div>

                      <div className="border-t pt-6">
                        <h3 className="text-lg font-medium mb-3">Contact Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input 
                              type="text" 
                              value={footerData.contactInfo?.phone || ''} 
                              onChange={(e) => {
                                setFooterData(prev => ({
                                  ...prev,
                                  contactInfo: { ...prev.contactInfo, phone: e.target.value }
                                }));
                              }} 
                              className="w-full border border-gray-300 rounded p-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input 
                              type="text" 
                              value={footerData.contactInfo?.email || ''} 
                              onChange={(e) => {
                                setFooterData(prev => ({
                                  ...prev,
                                  contactInfo: { ...prev.contactInfo, email: e.target.value }
                                }));
                              }} 
                              className="w-full border border-gray-300 rounded p-2 text-sm"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                            <input 
                              type="text" 
                              value={footerData.contactInfo?.address || ''} 
                              onChange={(e) => {
                                setFooterData(prev => ({
                                  ...prev,
                                  contactInfo: { ...prev.contactInfo, address: e.target.value }
                                }));
                              }} 
                              className="w-full border border-gray-300 rounded p-2 text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pt-6 border-t">
                        <button onClick={handleFooterUpdate} className="text-white px-8 py-3 rounded font-medium hover:opacity-80 transition-colors" style={{ backgroundColor: theme.primaryColor }}>
                          Save Footer Settings
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'settings' && (
                  <div className="animate-fade-in">
                    <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                      <h2 className="text-lg font-normal mb-4">Store Settings</h2>
                      <p className="text-sm text-gray-500 mb-4">Update the announcement text, free shipping threshold, sale box, visual search, logo, announcement bar, checkout sidebar color, continue shopping link, and COD shipping cost.</p>
                      <form onSubmit={handleSettingsSubmit} className="space-y-4 max-w-2xl">
                        <div>
                          <label className="block text-sm font-medium mb-1">Announcement Text</label>
                          <input 
                            value={announcementText} 
                            onChange={(e) => setAnnouncementText(e.target.value)} 
                            placeholder="e.g. Free shipping for orders over Rs.3,500" 
                            className="border p-3 rounded w-full outline-none focus:border-[#d60167]" 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Free Shipping Threshold (Rs.)</label>
                          <input 
                            type="number"
                            value={freeShippingThreshold} 
                            onChange={(e) => setFreeShippingThreshold(Number(e.target.value))} 
                            placeholder="e.g. 3500" 
                            className="border p-3 rounded w-full outline-none focus:border-[#d60167]" 
                            min="0"
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={saleBoxEnabled} 
                              onChange={(e) => setSaleBoxEnabled(e.target.checked)} 
                              className="w-5 h-5 rounded border-gray-300 text-[#d60167] focus:ring-[#d60167]" 
                            />
                            <span className="text-sm font-medium text-gray-700">Enable Sale Box</span>
                          </label>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Sale Box Content</label>
                          <input 
                            value={saleBoxContent} 
                            onChange={(e) => setSaleBoxContent(e.target.value)} 
                            placeholder="e.g. ⚠️ LAST WEEK OF SALE ⚠️" 
                            className="border p-3 rounded w-full outline-none focus:border-[#d60167]" 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Sale Box Color</label>
                          <input 
                            type="color"
                            value={saleBoxColor} 
                            onChange={(e) => setSaleBoxColor(e.target.value)} 
                            className="w-full h-12 border rounded cursor-pointer" 
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={visualSearchEnabled} 
                              onChange={(e) => setVisualSearchEnabled(e.target.checked)} 
                              className="w-5 h-5 rounded border-gray-300 text-[#d60167] focus:ring-[#d60167]" 
                            />
                            <span className="text-sm font-medium text-gray-700">Enable Visual Search</span>
                          </label>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Visual Search Icon Color</label>
                          <input 
                            type="color"
                            value={visualSearchColor} 
                            onChange={(e) => setVisualSearchColor(e.target.value)} 
                            className="w-full h-12 border rounded cursor-pointer" 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Logo</label>
                          <div className="flex items-center gap-4">
                            <img src={logoUrl} alt="Logo" className="h-12 object-contain border rounded" />
                            <div>
                              <input type="file" accept="image/*" onChange={(e) => handleLogoUpload(e.target.files[0])} disabled={uploadingLogo} className="border p-2 rounded w-full" />
                              {uploadingLogo && <span className="text-sm text-blue-600">Uploading...</span>}
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={announcementBarEnabled} 
                              onChange={(e) => setAnnouncementBarEnabled(e.target.checked)} 
                              className="w-5 h-5 rounded border-gray-300 text-[#d60167] focus:ring-[#d60167]" 
                            />
                            <span className="text-sm font-medium text-gray-700">Enable Announcement Bar</span>
                          </label>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Announcement Bar Text</label>
                          <input 
                            value={announcementBarText} 
                            onChange={(e) => setAnnouncementBarText(e.target.value)} 
                            placeholder="e.g. ONLINE EXCLUSIVE SALE IS LIVE NOW" 
                            className="border p-3 rounded w-full outline-none focus:border-[#d60167]" 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Announcement Bar Color</label>
                          <input 
                            type="color"
                            value={announcementBarColor} 
                            onChange={(e) => setAnnouncementBarColor(e.target.value)} 
                            className="w-full h-12 border rounded cursor-pointer" 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Checkout Sidebar Color</label>
                          <input 
                            type="color"
                            value={checkoutSidebarColor} 
                            onChange={(e) => setCheckoutSidebarColor(e.target.value)} 
                            className="w-full h-12 border rounded cursor-pointer" 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Continue Shopping Link</label>
                          <input 
                            type="text"
                            value={continueShoppingLink} 
                            onChange={(e) => setContinueShoppingLink(e.target.value)} 
                            placeholder="e.g. /category/new-in or /shop" 
                            className="border p-3 rounded w-full outline-none focus:border-[#d60167]" 
                          />
                          <p className="text-xs text-gray-500 mt-1">Enter a relative URL (e.g., /category/new-in) for "Continue Shopping" buttons.</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">COD Shipping Cost (Rs.)</label>
                          <input 
                            type="number"
                            value={shippingCost} 
                            onChange={(e) => setShippingCost(Number(e.target.value))} 
                            placeholder="e.g. 199" 
                            className="border p-3 rounded w-full outline-none focus:border-[#d60167]" 
                            min="0"
                          />
                          <p className="text-xs text-gray-500 mt-1">This is the flat shipping charge for Cash on Delivery orders.</p>
                        </div>
                        <button type="submit" disabled={savingSettings} className="text-white px-6 py-3 rounded hover:opacity-80 transition-colors" style={{ backgroundColor: theme.primaryColor }}>
                          {savingSettings ? 'Saving...' : 'Update Settings'}
                        </button>
                      </form>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <h2 className="text-lg font-normal mb-4">Payment Settings</h2>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={showCardPayment} 
                            onChange={handleCardPaymentToggle} 
                            className="w-5 h-5 rounded border-gray-300 text-[#d60167] focus:ring-[#d60167]" 
                          />
                          <span className="text-sm font-medium text-gray-700">Show Credit/Debit Card option in checkout</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'theme' && (
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h2 className="text-lg font-normal mb-4">Website Colors</h2>
                    <form onSubmit={handleThemeSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
                      <div>
                        <label className="block text-sm font-medium mb-2">Primary Color</label>
                        <input type="color" value={themeForm.primaryColor} onChange={(e) => setThemeForm({ ...themeForm, primaryColor: e.target.value })} className="w-full h-12 border rounded cursor-pointer" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Secondary Color</label>
                        <input type="color" value={themeForm.secondaryColor} onChange={(e) => setThemeForm({ ...themeForm, secondaryColor: e.target.value })} className="w-full h-12 border rounded cursor-pointer" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Background Color</label>
                        <input type="color" value={themeForm.backgroundColor} onChange={(e) => setThemeForm({ ...themeForm, backgroundColor: e.target.value })} className="w-full h-12 border rounded cursor-pointer" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Text Color</label>
                        <input type="color" value={themeForm.textColor} onChange={(e) => setThemeForm({ ...themeForm, textColor: e.target.value })} className="w-full h-12 border rounded cursor-pointer" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Border Color</label>
                        <input type="color" value={themeForm.borderColor} onChange={(e) => setThemeForm({ ...themeForm, borderColor: e.target.value })} className="w-full h-12 border rounded cursor-pointer" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Muted Text Color</label>
                        <input type="color" value={themeForm.mutedText} onChange={(e) => setThemeForm({ ...themeForm, mutedText: e.target.value })} className="w-full h-12 border rounded cursor-pointer" />
                      </div>
                      <div className="md:col-span-2">
                        <button type="submit" className="text-white px-8 py-3 rounded hover:opacity-80 transition-colors font-medium" style={{ backgroundColor: theme.primaryColor }}>Save Theme Colors</button>
                      </div>
                    </form>
                  </div>
                )}

                {activeTab === 'users' && (
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h2 className="text-lg font-normal mb-4">All Users</h2>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b bg-gray-50 text-sm">
                            <th className="p-3 font-medium">Name</th>
                            <th className="p-3 font-medium">Email</th>
                            <th className="p-3 font-medium">Role</th>
                            <th className="p-3 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map(u => (
                            <tr key={u._id} className="border-b">
                              <td className="p-3 text-sm">{u.name}</td>
                              <td className="p-3 text-sm">{u.email}</td>
                              <td className="p-3">
                                <select value={u.role} onChange={(e) => handleUserUpdate(u, { role: e.target.value })} className="border p-2 rounded bg-gray-50 outline-none text-sm">
                                  <option value="user">User</option><option value="admin">Admin</option><option value="sub-admin">Sub-Admin</option>
                                </select>
                              </td>
                              <td className="p-3">
                                <select value={u.isActive === false ? 'inactive' : 'active'} onChange={(e) => handleUserUpdate(u, { isActive: e.target.value === 'active' })} className="border p-2 rounded bg-gray-50 outline-none text-sm">
                                  <option value="active">Active</option><option value="inactive">Inactive</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === 'newsletter' && (
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h2 className="text-lg font-normal mb-4">Newsletter Subscribers</h2>
                    {subscribers.length === 0 ? (
                      <p className="text-gray-500">No subscribers yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left">
                          <thead>
                            <tr className="border-b bg-gray-50 text-gray-600 text-sm">
                              <th className="p-3 font-medium">Email</th>
                              <th className="p-3 font-medium">Subscribed Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subscribers.map(sub => (
                              <tr key={sub._id} className="border-b">
                                <td className="p-3 text-sm">{sub.email}</td>
                                <td className="p-3 text-sm">{new Date(sub.subscribedAt).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {!isAdminOrSubAdmin && activeTab === 'profile' && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-normal mb-4">My Profile</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border p-4 rounded"><p className="text-sm text-gray-500">Name</p><h3 className="text-xl text-gray-900">{user?.name || '-'}</h3></div>
                  <div className="border p-4 rounded"><p className="text-sm text-gray-500">Email</p><h3 className="text-xl text-gray-900">{user?.email || '-'}</h3></div>
                  <div className="border p-4 rounded"><p className="text-sm text-gray-500">Phone</p><h3 className="text-xl text-gray-900">{user?.phone || '-'}</h3></div>
                  <div className="border p-4 rounded"><p className="text-sm text-gray-500">Address</p><h3 className="text-xl text-gray-900">{user?.address || '-'}</h3></div>
                </div>
                <Link to="/profile" className="inline-block mt-6 text-white px-6 py-3 rounded hover:opacity-80 transition-colors font-medium" style={{ backgroundColor: theme.primaryColor }}>Edit Profile</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;